import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { BrandLogo } from "./BrandLogo";

const navLinks = [
  { to: "/proyecto", label: "Proyecto" },
  { to: "/programas", label: "Programas" },
  { to: "/noticias", label: "Noticias" },
  { to: "/galeria", label: "Galería" },
  { to: "/contacto", label: "Contacto" },
];

// URL del formulario/aplicación de inscripción. Vive fuera de la SPA
// (en /inscripcion/) — la sirve otra app en el mismo dominio, así que
// no necesita target="_blank": el usuario navega y puede volver con
// "atrás" del navegador si necesita.
const INSCRIPCION_URL = "https://campusemjc.unex.es/inscripcion/";

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Cerrar el menú al cambiar de ruta (cuando el usuario click un link).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Cerrar con tecla Escape y devolver el foco al botón hamburguesa.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Bloquear el scroll del body cuando el menú está abierto.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-30 border-b border-gray-300 bg-brand-paper/95 backdrop-blur supports-[backdrop-filter]:bg-brand-paper/80">
      <div className="container-emjc flex items-center justify-between py-4">
        <Link to="/" className="flex items-center" aria-label="Inicio Campus EMJC">
          {/* El logo ya incluye "Escuela Municipal de Jóvenes Científicos",
              así que no necesitamos el span de texto al lado. Altura 48 en
              móvil, 56 en escritorio para mantener proporciones del header. */}
          <BrandLogo height={48} className="md:hidden" />
          <BrandLogo height={56} className="hidden md:block" />
        </Link>

        {/* Navegación de escritorio (≥768px) */}
        <nav aria-label="Principal" className="hidden md:block">
          <ul className="flex items-center gap-7">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    `font-medium text-[15px] transition-colors hover:text-brand-orange-deep ${
                      isActive ? "text-brand-orange-deep" : "text-brand-ink"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
            <li>
              <a
                href={INSCRIPCION_URL}
                className="rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-brand-paper transition-colors hover:bg-brand-orange"
              >
                Matricúlate
              </a>
            </li>
          </ul>
        </nav>

        {/* Botón hamburguesa: solo en móvil. Cuando el menú está abierto se
            transforma visualmente en una "X". El icono está hecho con líneas
            CSS animadas para evitar dependencias de iconos externos. */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-ink transition-colors hover:bg-brand-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange md:hidden"
        >
          <BurgerIcon open={mobileOpen} />
        </button>
      </div>

      {/* Panel desplegable de móvil. Aparece justo debajo del header,
          ocupando el ancho completo. No usamos overlay completo para que
          el header sticky siga visible y el usuario pueda cerrar con la X. */}
      <nav
        id="mobile-nav"
        aria-label="Principal (móvil)"
        className={`md:hidden ${
          mobileOpen
            ? "block border-t border-gray-300 bg-brand-paper"
            : "hidden"
        }`}
      >
        <ul className="container-emjc flex flex-col gap-1 py-4">
          {navLinks.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? "bg-brand-orange-soft text-brand-orange-deep"
                      : "text-brand-ink hover:bg-brand-paper hover:text-brand-orange-deep"
                  }`
                }
              >
                {link.label}
              </NavLink>
            </li>
          ))}
          <li className="mt-2">
            <a
              href={INSCRIPCION_URL}
              onClick={() => setMobileOpen(false)}
              className="block rounded-full bg-brand-ink px-4 py-3 text-center text-sm font-semibold text-brand-paper transition-colors hover:bg-brand-orange"
            >
              Matricúlate
            </a>
          </li>
        </ul>
      </nav>
    </header>
  );
}

/**
 * Icono accesible: 3 líneas horizontales (menú) que al abrir se transforman
 * en X. Las 3 líneas mantienen aria-hidden porque el botón ya tiene
 * aria-label semántico.
 */
function BurgerIcon({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="relative block h-5 w-6"
    >
      <span
        className={`absolute left-0 right-0 block h-0.5 bg-current transition-all duration-200 ${
          open ? "top-1/2 -translate-y-1/2 rotate-45" : "top-1"
        }`}
      />
      <span
        className={`absolute left-0 right-0 top-1/2 block h-0.5 -translate-y-1/2 bg-current transition-opacity duration-200 ${
          open ? "opacity-0" : "opacity-100"
        }`}
      />
      <span
        className={`absolute left-0 right-0 block h-0.5 bg-current transition-all duration-200 ${
          open ? "top-1/2 -translate-y-1/2 -rotate-45" : "bottom-1"
        }`}
      />
    </span>
  );
}
